/**
 * The platform's dialog anatomy (owner, 19 Oct 2026: D1) — one set of classes
 * for every dialog and confirmation, so none is assembled by hand again:
 *
 *   header  the media (an icon tile, or a partner's mark), the title, one line
 *           of description; a line under it;
 *   steps   a wizard's step strip, under the header;
 *   body    24px all round, scrolling between a header and a footer that stay;
 *   footer  a bar: the way out on the left, ONE main action on the right.
 *
 * Sizes are four, named by what they hold, instead of a width per file.
 */
export const DIALOG_BAR = {
  header:
    'shrink-0 flex flex-row items-center gap-4 space-y-0 border-b px-6 pt-6 pb-5 pr-12 text-left',
  steps: 'shrink-0 border-b px-6 py-4',
  body: 'min-h-0 flex-1 space-y-5 overflow-y-auto p-6',
  footer:
    'shrink-0 flex flex-row items-center justify-between gap-3 border-t bg-muted/30 px-6 py-3',
} as const;

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

/** sm: a confirmation · md: a short form · lg: a form, a reading · xl: a wizard. */
export const DIALOG_SIZE: Record<DialogSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-3xl',
};

export type DialogMediaIntent = 'tone' | 'caution' | 'danger' | 'success';

/**
 * The header's icon tile. `tone` is the scope's (the hero's, or the platform's
 * outside a hero) — the accent family a ToneScope redefines.
 */
export const DIALOG_MEDIA: Record<DialogMediaIntent, string> = {
  tone: 'bg-accent text-accent-foreground',
  caution: 'bg-tone-amber-subtle text-tone-amber',
  danger: 'bg-tone-red-subtle text-tone-red',
  success: 'bg-tone-green-subtle text-tone-green',
};
